import React from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Alert, Box, CircularProgress, Collapse, Stack, TableCell, TableRow, Typography } from '@mui/material';

import type { Dayjs } from '~/services/TimeService';

import { dropdownContentSx, getDropdownCellSx, handleExpandRowKeyDown } from '../surcharges/surchargeTabLayout';

import UnmatchedBlackoutRoomRateCards from './UnmatchedBlackoutRoomRateCards';
import type { RoomRateBlackoutAssignment } from './appliedBlackoutUtils';

interface Props {
  assignments: Array<RoomRateBlackoutAssignment>;
  columnCount: number;
  error?: unknown;
  isExpanded: boolean;
  isLoading: boolean;
  now: Dayjs;
  onToggleExpanded: () => void;
}

export default function UnmatchedBlackoutSection({
  assignments,
  columnCount,
  error,
  isExpanded,
  isLoading,
  now,
  onToggleExpanded,
}: Props) {
  return (
    <>
      <TableRow
        hover
        role="button"
        sx={{ cursor: 'pointer', '& .MuiTableCell-root': { bgcolor: 'grey.50' } }}
        tabIndex={0}
        onClick={onToggleExpanded}
        onKeyDown={(event) => handleExpandRowKeyDown(event, onToggleExpanded)}
      >
        <TableCell>{isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}</TableCell>
        <TableCell colSpan={columnCount - 1}>
          <Typography fontWeight={700} variant="body2">
            Room Rate Blackouts with No Matching Salesforce Blackout
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={columnCount} sx={getDropdownCellSx(isExpanded)}>
          <Collapse unmountOnExit in={isExpanded} sx={{ width: '100%' }} timeout="auto">
            <Box sx={dropdownContentSx}>
              {isLoading ? (
                <Stack alignItems="center" direction="row" justifyContent="center" spacing={1} sx={{ py: 2 }}>
                  <CircularProgress size={16} />
                  <Typography color="text.secondary" variant="body2">
                    Loading room-rate blackout dates.
                  </Typography>
                </Stack>
              ) : error ? (
                <Alert severity="error">Unable to load room-rate blackout dates.</Alert>
              ) : (
                <UnmatchedBlackoutRoomRateCards assignments={assignments} now={now} />
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
